/**
 * Per-collection driver routing + other storage feature flags.
 *
 * This file controls which backend each collection uses. Flags are
 * configurable at runtime via localStorage so the developer can flip routing
 * without editing code or rebuilding.
 *
 * Values: 'convex' | 'local'. Any path set to 'local' uses the in-memory
 * driver regardless of other settings. (The 'firebase' option was removed in
 * Session 10 — 2026-04-20 — once the data migration was complete.)
 *
 * Historical context retained below: the SESSION_N_CONVEX_PATHS arrays
 * record which paths were migrated in each session. Post-Session-10 they're
 * all 'convex' by default, so the lists are descriptive rather than
 * load-bearing. Kept as provenance for future readers.
 */

import { COLLECTION_PATHS } from './StorageDriver.js';

const LS_KEY = 'bmg_convex_routing';

// The 9 paths Session 3 migrates when Convex routing is enabled.
const SESSION_3_CONVEX_PATHS = [
  'diets', 'diseases', 'foodTypes', 'locations',
  'spellLibrary', 'npcLibrary',
  'globalSettings', 'currentLocation', 'journalSettings',
];

// Session 4 adds chars + systems to Session 3's paths. Cumulative.
const SESSION_4_CONVEX_PATHS = [...SESSION_3_CONVEX_PATHS, 'chars', 'systems'];

// Session 5 adds combat + snapshots + journal + dmJournal + audit. Cumulative.
const SESSION_5_CONVEX_PATHS = [...SESSION_4_CONVEX_PATHS,
  'combat', 'combatSnapshots', 'journal', 'dmJournal', 'audit',
];

// Session 6 adds the last 3 paths. All 18 now on Convex.
const SESSION_6_CONVEX_PATHS = [...SESSION_5_CONVEX_PATHS,
  'messages', 'groupTravelLog', 'food',
];

// Valid localStorage values for routing mode.
const VALID_MODES = new Set(['off', 'session-3', 'session-4', 'session-5', 'session-6']);

// Read the persisted routing mode from localStorage. Returns the stored mode
// if valid, else falls back to `REACT_APP_DEFAULT_ROUTING` env var (so the
// prod Vercel build can ship with session-6 routing as default), else 'off'.
// Safely returns 'off' on SSR / disabled storage.
//
// Session 9 (2026-04-20): env-var fallback lets the prod Vercel build default
// to `session-6` without hardcoding it, while the dev build keeps `off`
// unless localStorage explicitly flips it. Users with an existing localStorage
// preference still win — so your dev-routed state survives code changes.
const readPersistedMode = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'off';
    const v = window.localStorage.getItem(LS_KEY);
    if (v && VALID_MODES.has(v)) return v;
    const envDefault = process.env.REACT_APP_DEFAULT_ROUTING;
    if (envDefault && VALID_MODES.has(envDefault)) return envDefault;
    return 'off';
  } catch (_) {
    return 'off';
  }
};

const currentMode = readPersistedMode();

// Build the routing map based on the current mode.
//
// Session 10 (2026-04-20): Firebase driver deleted. Base initialization changed
// from 'firebase' to 'convex' so any path without an explicit assignment
// defaults to Convex. The session-3/4/5/6 path lists are kept for historical
// reference (so someone reading this file can trace the migration) but are
// now equivalent to the base — they assign 'convex' to a subset of paths that
// are already 'convex' by default. This is a no-op redundancy rather than
// dead code; removing the lists would lose the provenance trail.
const buildRouting = (mode) => {
  const base = Object.fromEntries(COLLECTION_PATHS.map(p => [p, 'convex']));
  const paths = mode === 'session-6' ? SESSION_6_CONVEX_PATHS
              : mode === 'session-5' ? SESSION_5_CONVEX_PATHS
              : mode === 'session-4' ? SESSION_4_CONVEX_PATHS
              : mode === 'session-3' ? SESSION_3_CONVEX_PATHS
              : [];
  paths.forEach(p => { base[p] = 'convex'; });
  return base;
};

export const collectionRouting = /** @type {Record<string, 'convex'|'local'>} */ (buildRouting(currentMode));

// Session 8 (2026-04-19): Convex Auth flag. Independent of Convex routing —
// auth controls identity (Google OAuth vs demo-auth passwords), routing
// controls which backend holds data. Default OFF so demo-auth (DM_PASSWORD +
// per-char passwords) continues to work until the user explicitly flips to
// auth-backed mode or the env-var default kicks in.
//
// Session 9 (2026-04-20): added env-var fallback `REACT_APP_DEFAULT_USE_AUTH`
// so the prod Vercel build can ship with auth on by default. Users with an
// explicit localStorage preference ('1' or '0') still win — this fallback
// only applies when localStorage has no entry at all.
const AUTH_LS_KEY = 'bmg_use_convex_auth';
const readAuthFlag = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const stored = window.localStorage.getItem(AUTH_LS_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    // No explicit preference → env-var default (prod=on, dev=off).
    return process.env.REACT_APP_DEFAULT_USE_AUTH === '1';
  } catch (_) {
    return false;
  }
};

export const FEATURE_FLAGS = {
  // True whenever the routing mode is not 'off'. useStorage branches on this
  // to decide whether to compose via RoutedDriver. Post-Session-10 with
  // Firebase gone, the only difference between modes is cosmetic (the
  // routing map entries). Retained for a possible future multi-backend
  // scenario (e.g., per-campaign sharding) without re-introducing the flag.
  useRoutedDriver: currentMode !== 'off',
  // Session 8: when true, App.js renders the Google-OAuth login screen
  // instead of the demo-auth password prompts. Demo-auth path preserved
  // for `?local=1` and rollback.
  useConvexAuth: readAuthFlag(),
};

/**
 * Dev helper: enable Convex Auth (Google OAuth). Persists via localStorage
 * and reloads the page so App.js picks up the new flag at startup.
 */
export const enableConvexAuth = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTH_LS_KEY, '1');
  console.log('Convex Auth enabled. Reloading...');
  window.location.reload();
};

/**
 * Dev helper: disable Convex Auth, revert to demo-auth (DM_PASSWORD +
 * per-char passwords). Rollback path from Session 8.
 *
 * Writes '0' explicitly instead of removeItem so the env-var default
 * (REACT_APP_DEFAULT_USE_AUTH=1 on prod) doesn't sneak auth back on after
 * a reload. `localStorage` preference overrides env default.
 */
export const disableConvexAuth = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTH_LS_KEY, '0');
  console.log('Convex Auth disabled. Reloading...');
  window.location.reload();
};

/**
 * Dev helper: read the routing for one path.
 * @param {string} path
 */
export const getRouting = (path) => collectionRouting[path] ?? 'convex';

/**
 * Dev helper: enable Session 3's Convex routing (9 low-churn paths). Persists
 * via localStorage and reloads the page so drivers reinitialize.
 */
export const enableConvexRouting = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, 'session-3');
  console.log('Convex routing enabled for Session 3 paths. Reloading...');
  window.location.reload();
};

/**
 * Dev helper: enable Session 4's Convex routing (Session 3 paths + chars + systems).
 * Cumulative over session-3; flipping directly here works even if user skipped
 * session-3 mode, but only makes sense after seedConvexChars has populated those
 * tables.
 */
export const enableConvexRoutingSession4 = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, 'session-4');
  console.log('Convex routing enabled for Session 4 paths (chars + systems). Reloading...');
  window.location.reload();
};

/**
 * Dev helper: enable Session 5's Convex routing (Session 4 paths + combat +
 * combatSnapshots + journal + dmJournal + audit). Cumulative. Only makes sense
 * after seedCombatJournalAudit has populated those singleton tables.
 */
export const enableConvexRoutingSession5 = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, 'session-5');
  console.log('Convex routing enabled for Session 5 paths (+ combat/journal/audit). Reloading...');
  window.location.reload();
};

/**
 * Dev helper: enable Session 6's Convex routing — adds messages + groupTravelLog
 * + food. All 18 collections routed to Convex when this mode is active.
 * Only makes sense after seedRemaining has populated those singletons.
 */
export const enableConvexRoutingSession6 = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, 'session-6');
  console.log('Convex routing enabled for Session 6 paths (all 18 collections on Convex). Reloading...');
  window.location.reload();
};

/**
 * Dev helper: disable the historical routed-driver wrapper. Firebase was
 * removed in Session 10; explicit local/demo mode is handled by `?local=1`
 * or `window.__LOCAL_ONLY__`, not by this routing flag.
 */
export const disableConvexRouting = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, 'off');
  console.log('Convex routing disabled. Reloading...');
  window.location.reload();
};
